import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CSSTransition } from 'react-transition-group';
import onResize from 'simple-element-resize-detector';
import { toastr } from "react-redux-toastr";
import moment from "moment";

import NoZonesComponent from "./NoZonesComponent";
import ZonesPopover from "./ZonesPopover";
import { getCompanyZones } from "../../../action/CompaniesActions";
import CustomButton from "../../CustomButton";
import { ReactComponent as ZonesBtnIcon } from '../../../../../assets/zones-btn.svg';

export default function ZonesMapComponent() {
    const dispatch = useDispatch();
    const { t } = useTranslation('translation', { keyPrefix: 'zonesPage' });
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const behaviorRef = useRef(null);
    const platformRef = useRef(null);
    const [mapUI, setMapUI] = useState(null);
    const [mapState, setMapState] = useState({
        lat: 37.09024,
        lng: -95.712891,
        zoom: 12,
    });
    const [zones, setZones] = useState([]);
    const [mode, setMode] = useState('');
    const [mapPopOver, setMapPopOver] = useState(true);
    const user = useSelector(state => state.user.user);
    const company = useSelector(state => state.company.company);
    const companyId = company.company_id;
    const [searchText, setSearchText] = useState('');
    const [editedZone, setEditedZone] = useState(null);
    const [highlightedZoneId, setHighlightedZoneId] = useState(null);
    const { settings = {} } = user;
    const userTZ = settings.time_zone || null;
    const filteredZones = useMemo(() => {
        return zones.filter(zone =>
            zone.zone_name.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [zones, searchText]);

    useEffect(() => {
        if (companyId) {
            toggleLoader(true);
            getZones();
        }
    }, [companyId]);

    useEffect(() => {
        if (!highlightedZoneId) return;

        const timer = setTimeout(() => {
            setHighlightedZoneId(null);
        }, 3000);

        return () => clearTimeout(timer);
    }, [highlightedZoneId]);

    const getZones = (stayAtZone) => {
        const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = {
            date: moment().format('YYYYMMDDhhmmss'),
            tzname: userTZ ? userTZ : localTZ,
        };
        getCompanyZones(companyId, params).then(res => {
            const map = mapInstanceRef?.current;
            const zonesList = res.data.response || [];
            setZones(zonesList);
            setMode(zonesList.length ? 'LIST' : 'NO_ZONES');
            if (map && !stayAtZone) {
                if (zonesList.length > 0) {
                    const allZonesObject = map.getObjects().find(
                        obj => obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
                    );
                    map.getViewModel().setLookAtData({ bounds: allZonesObject?.getBoundingBox() });
                }
            }
            toggleLoader(false);
        }).catch(err => {
            console.log(err, "get Zones error");
            toastr.error(t('errorLoadingZones'))
            toggleLoader(false);
        });
    }

    useEffect(() => {
        if (!mapInstanceRef.current) {
            const platform = new H.service.Platform({
                apikey: process.env.REACT_APP_HERE_MAPS_API_KEY
            });
            platformRef.current = platform;
            const layers = platform.createDefaultLayers();
            const baseProvider = layers.vector.normal.map.getProvider();
            const noWrapLayer = new H.map.layer.TileLayer(baseProvider, {
                worldWrap: false,
                max: 22
            });
            const MAP = new H.Map(mapRef.current, noWrapLayer, {
                center: {
                    lat: mapState.lat,
                    lng: mapState.lng
                },
                zoom: mapState.zoom,
                pixelRatio: window.devicePixelRatio || 1
            });

            onResize(mapRef.current, () => MAP.getViewPort().resize());
            MAP.addEventListener('mapviewchange', handleMapViewChange);
            behaviorRef.current = new H.mapevents.Behavior(new H.mapevents.MapEvents(MAP));
            const ui = H.ui.UI.createDefault(MAP, layers);
            ui.getControl('zoom').setAlignment('right-top');
            setMapUI(ui);

            mapInstanceRef.current = MAP;
        }

        return () => {
            mapInstanceRef.current?.removeEventListener('mapviewchange', handleMapViewChange);
        }
    }, []);

    useEffect(() => {
        if (mapInstanceRef?.current && mapUI?.current) {
            mapUI.getControl('zoom').setAlignment('left-top');
        }

        return () => {
            if (mapInstanceRef?.current && mapUI?.current) {
                mapUI.current.getControl('zoom').setAlignment('right-top');
                mapInstanceRef.current.removeObjects(mapInstanceRef.current.getObjects().filter(e => e && e instanceof H.map.Group));
            }
        }
    }, []);

    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map) return;

        const allZonesGroup = map.getObjects().find(obj =>
            obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
        );
        if (allZonesGroup) {
            allZonesGroup.getObjects().forEach(zoneGroup => {
                zoneGroup.getObjects().forEach(obj => {
                    if (obj instanceof H.map.DomMarker) {
                        obj.setVisibility(mapState.zoom >= 10);
                    }
                });
            });
        }
    }, [filteredZones, mapState.zoom]);

    useEffect(() => {
        const map = mapInstanceRef?.current;
        if (!map) return;

        const zoneGroups = map.getObjects().filter(obj =>
            obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
        );
        map.removeObjects(zoneGroups);

        const allZonesObject = new H.map.Group();
        allZonesObject.setRemoteId('allZonesObject');

        const k = (v, radius) => Math.round(v / 2 + radius * v / 10);

        filteredZones.forEach(zone => {
            const position = { lat: zone.lat, lng: zone.lon };
            const div = document.createElement('div');
            const label = document.createElement('label');
            div.setAttribute('class', 'markerZone');

            label.style.fontSize = `${k(18, zone.radius)}px`;
            label.style.marginTop = `25px`;
            label.innerText = zone.zone_name;
            div.appendChild(label);

            const domIcon = new H.map.DomIcon(div);
            const textLabel = new H.map.DomMarker(position, { icon: domIcon });
            textLabel.setData({ domElement: div });

            const icon = new H.map.Icon('/images/markerZone.svg', { size: { w: k(38, zone.radius), h: k(55, zone.radius) } })
            const marker = new H.map.Marker(position, { icon });

            const circle = new H.map.Circle(position, (zone.radius * 1609.34), {
                style: { lineWidth: 0, fillColor: "rgba(241, 4, 4, 0.25)" }
            });
            const circleOutline = new H.map.Polyline(circle.getGeometry().getExterior(), {
                style: { lineWidth: 20, strokeColor: "rgba(255, 215, 0, 0.25)" }
            });
            circleOutline.getGeometry().pushPoint(circleOutline.getGeometry().extractPoint(0));

            const group = new H.map.Group();
            group.addObjects([marker, textLabel, circle, circleOutline]);
            group.setRemoteId(zone.id);

            group.addEventListener('tap', () => handleMapClick(circleOutline, zone.id));
            changeStyle(circleOutline);
            allZonesObject.addObject(group);
        });
        map.addObject(allZonesObject);

        map.getViewPort().setPadding(15, (window.innerWidth > 568 ? 238 : 182), 15, 15);
    }, [filteredZones]);

    const handleMapClick = (circleOutline, zoneId) => {
        changeStyle(circleOutline);
        setHighlightedZoneId(zoneId);
    };

    const changeStyle = (circleOutline) => {
        circleOutline.setVisibility(true);

        circleOutline.setStyle({
            lineWidth: 20,
            strokeColor: "rgba(255, 215, 0, 0.25)"
        });

        setTimeout(() => {
            setTimeout(() => {
                fadeOutCircle(circleOutline, 2000);
            }, 3000);
        }, 50);
    };

    const fadeOutCircle = (circleOutline, duration = 2000) => {
        const start = performance.now();
        const initialAlpha = 0.25;

        const animate = (time) => {
            const elapsed = time - start;
            const progress = Math.min(elapsed / duration, 1);
            const newAlpha = initialAlpha * (1 - progress);

            circleOutline.setStyle({
                lineWidth: 20,
                strokeColor: `rgba(255, 215, 0, ${newAlpha})`
            });

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                circleOutline.setVisibility(false);
            }
        };

        requestAnimationFrame(animate);
    };

    const handleMapViewChange = (e) => {
        if (e.newValue?.lookAt) {
            const { position, zoom } = e.newValue.lookAt;
            const lat = Math.trunc(position.lat * 1e7) / 1e7;
            const lng = Math.trunc(position.lng * 1e7) / 1e7;
            const z = Math.trunc(zoom * 1e2) / 1e2;
            setMapState({ lat, lng, zoom: z });
        }
    };

    const getZoom = (radius) => {
        if (radius >= 0.01 && radius <= 0.5) {
            return 15;
        } else if (radius > 0.5 && radius <= 1) {
            return 14;
        } else if (radius > 1 && radius <= 2) {
            return 13;
        }
        return 12;
    }

    const highlightZoneOnMap = (id) => {
        const map = mapInstanceRef?.current;
        if (!map) return;

        const allZonesGroup = map.getObjects().find(
            obj => obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
        );
        const zoneGroup = allZonesGroup.getObjects().find(
            obj => obj instanceof H.map.Group && obj.getRemoteId() === id
        );
        if (zoneGroup) {
            zoneGroup.getObjects().forEach(obj => {
                if (obj instanceof H.map.Polyline) {
                    const boundingBox = zoneGroup.getBoundingBox();
                    const center = boundingBox.getCenter();
                    const handleViewChangeEnd = () => {
                        changeStyle(obj);
                        map.removeEventListener('mapviewchangeend', handleViewChangeEnd);
                    };

                    map.addEventListener('mapviewchangeend', handleViewChangeEnd);
                    map.getViewModel().setLookAtData({
                        position: { lat: center.lat, lng: center.lng },
                        zoom: 8
                    }, true);
                }
            });
        }
    };

    const toggleLoader = useCallback((show) => {
        dispatch({ type: 'TOGGLE_LOADER', payload: show });
    }, [dispatch]);

    return (
        <div className="map-wrapper">
            <div
                id="map"
                style={{ position: 'relative', width: '100%', height: 'calc(100vh - 67px)' }}
                ref={mapRef}
            ></div>
            {['LIST', 'CREATE', 'EDIT'].includes(mode) ? (
                <>
                    <CustomButton
                        variant={mapPopOver ? "primary-outline" : "primary"}
                        size="icon"
                        prefix="zones-popover-button"
                        onClick={() => setMapPopOver(!mapPopOver)}
                    >
                        <ZonesBtnIcon />
                    </CustomButton>
                    <CSSTransition
                        in={mapPopOver}
                        timeout={400}
                        classNames="popover-transition"
                        unmountOnExit
                    >
                        <div>
                            <ZonesPopover
                                mode={mode}
                                setMode={setMode}
                                zones={zones}
                                searchText={searchText}
                                setSearchText={setSearchText}
                                filteredZones={filteredZones}
                                getZones={getZones}
                                getZoom={getZoom}
                                map={mapInstanceRef?.current}
                                behavior={behaviorRef?.current}
                                mapState={mapState}
                                platform={platformRef?.current}
                                editedZone={editedZone}
                                setEditedZone={setEditedZone}
                                highlightedZoneId={highlightedZoneId}
                                highlightZoneOnMap={highlightZoneOnMap}
                            />
                        </div>
                    </CSSTransition>
                </>
            ) : ''}
            {mode === 'NO_ZONES' ? (
                <NoZonesComponent setMode={setMode} />
            ) : ''}
        </div>
    )
}
